import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from '../../lib/zod';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '@workspace/ui';
import { API_URL } from '../../lib/env';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const createSchema = (t: TFunction<'identity'>) => z
  .object({
    password: z
      .string()
      .min(8, t('auth.validation.passwordMin8'))
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        t('auth.validation.passwordComplexity')
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: t('auth.validation.passwordMismatch'),
    path: ['confirmPassword'],
  });

type ResetPasswordForm = { password: string; confirmPassword: string };

export function ResetPasswordPage() {
  const { t } = useTranslation('identity');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const schema = useMemo(() => createSchema(t), [t]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver,
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!token) {
      setError(t('auth.reset.invalidLink'));
      return;
    }

    setError(null);
    try {
      await axios.post(`${API_URL}/auth/password/reset/${token}`, {
        password: values.password,
      });
      setSuccess(true);
    } catch {
      setError(t('auth.reset.resetFailed'));
    }
  });

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 text-center shadow-surface">
          <h1 className="mb-4 text-2xl font-semibold text-content-primary">
            {t('auth.reset.invalidTitle')}
          </h1>
          <p className="mb-6 text-content-secondary">
            {t('auth.reset.invalidDescription')}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('auth.common.backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 text-center shadow-surface">
          <h1 className="mb-4 text-2xl font-semibold text-content-primary">
            {t('auth.reset.successTitle')}
          </h1>
          <p className="mb-6 text-content-secondary">
            {t('auth.reset.successDescription')}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('auth.common.signIn')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 shadow-surface">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-content-primary">
            {t('auth.reset.title')}
          </h1>
          <p className="mt-2 text-sm text-content-muted">
            {t('auth.reset.subtitle')}
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.reset.newPassword')}
            </label>
            <Input type="password" {...register('password')} />
            {errors.password && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.password.message}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.common.confirmPassword')}
            </label>
            <Input type="password" {...register('confirmPassword')} />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && <p className="text-sm text-state-danger">{error}</p>}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.reset.saving') : t('auth.reset.save')}
          </Button>
        </form>
      </div>
    </div>
  );
}
